import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "wouter";
import { TimelineShell } from "@/components/ui/TimelineShell";
import { appShell } from "@/store";
import {
  fetchIrrigationPlanHistoryAtom,
  irrigationPlanHistoryErrorAtom,
  irrigationPlanHistoryFiltersAtom,
  irrigationPlanHistoryHasMoreAtom,
  irrigationPlanHistoryLoadingAtom,
  irrigationPlanHistoryPaginationAtom,
  irrigationPlanHistoryRequestKeyAtom,
  irrigationPlanHistorySelectionReadyAtom,
  irrigationPlanHistoryTimelineAtom,
} from "@/store/data";
import { dayjs } from "@/utils/date";
import { HistoryFiltersBar, HistorySummaryCards, TimelineEntryCard } from "./components";

function IrrigationPlanHistoryPage() {
  const setBackButton = useSetAtom(appShell.backButtonAtom);
  const setFilters = useSetAtom(irrigationPlanHistoryFiltersAtom);
  const fetchHistory = useSetAtom(fetchIrrigationPlanHistoryAtom);
  const selectionReady = useAtomValue(irrigationPlanHistorySelectionReadyAtom);
  const requestKey = useAtomValue(irrigationPlanHistoryRequestKeyAtom);
  const hasMore = useAtomValue(irrigationPlanHistoryHasMoreAtom);
  const loadingHistory = useAtomValue(irrigationPlanHistoryLoadingAtom);
  const timelineEntries = useAtomValue(irrigationPlanHistoryTimelineAtom);
  const timelineError = useAtomValue(irrigationPlanHistoryErrorAtom);
  const setPagination = useSetAtom(irrigationPlanHistoryPaginationAtom);
  const [searchParams] = useSearchParams();
  const lastAppliedParams = useRef<string | null>(null);
  const searchParamsKey = searchParams.toString();

  useEffect(() => {
    if (searchParamsKey === lastAppliedParams.current) return;
    lastAppliedParams.current = searchParamsKey;

    const params = new URLSearchParams(searchParamsKey);
    const scopeParam = params.get("scope");
    const hasProjectParam = params.has("projectId");
    const projectParam = hasProjectParam ? params.get("projectId") : null;
    const hasPlanParam = params.has("planId");
    const planParam = hasPlanParam ? params.get("planId") : null;
    const hasStartParam = params.has("start");
    const startParam = hasStartParam ? params.get("start") : null;
    const hasEndParam = params.has("end");
    const endParam = hasEndParam ? params.get("end") : null;

    setFilters((prev) => {
      let nextScope = prev.scope;
      if (scopeParam === "plan" || scopeParam === "project") {
        nextScope = scopeParam;
      }

      let nextProjectId = prev.projectId;
      if (hasProjectParam) {
        nextProjectId = projectParam ? projectParam : null;
      }

      let nextPlanId = nextScope === "plan" ? prev.planId : null;
      if (nextScope === "plan") {
        if (hasPlanParam) {
          nextPlanId = planParam ? planParam : null;
        }
      } else {
        nextPlanId = null;
      }

      let nextStartDate = prev.startDate;
      if (hasStartParam && startParam) {
        const start = dayjs(startParam);
        if (start.isValid()) {
          const iso = start.toISOString();
          if (iso !== nextStartDate) {
            nextStartDate = iso;
          }
        }
      }

      let nextEndDate = prev.endDate;
      if (hasEndParam && endParam) {
        const end = dayjs(endParam);
        if (end.isValid()) {
          const iso = end.toISOString();
          if (iso !== nextEndDate) {
            nextEndDate = iso;
          }
        }
      }

      const changed =
        nextScope !== prev.scope ||
        nextProjectId !== prev.projectId ||
        nextPlanId !== prev.planId ||
        nextStartDate !== prev.startDate ||
        nextEndDate !== prev.endDate;

      if (!changed) {
        return prev;
      }

      return {
        ...prev,
        scope: nextScope,
        projectId: nextProjectId,
        planId: nextPlanId,
        startDate: nextStartDate,
        endDate: nextEndDate,
      };
    });
  }, [searchParamsKey, setFilters]);

  useEffect(() => {
    setBackButton("/app/reports");
    return () => {
      setBackButton(false);
    };
  }, [setBackButton]);

  useEffect(() => {
    if (!selectionReady) return;
    fetchHistory();
  }, [fetchHistory, requestKey, selectionReady]);

  const handleLoadMore = useCallback(() => {
    if (loadingHistory) return;
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, [loadingHistory, setPagination]);

  const handleRetry = useCallback(() => {
    if (!selectionReady) return;
    fetchHistory();
  }, [fetchHistory, selectionReady]);

  return (
    <div className="min-h-full space-y-6 bg-gray-50 p-6 pb-20">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">Histórico de planos de irrigação</h1>
        <p className="text-gray-600">Visualize a evolução dos planos de irrigação e acompanhe mudanças relevantes.</p>
      </header>

      <section className="rounded-xl border border-neutral-200 bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">Controles de escopo e filtros</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Defina o escopo de análise, selecione projeto ou plano e ajuste o intervalo de datas do relatório.
        </p>
        <div className="mt-4">
          <HistoryFiltersBar />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Resumo</h2>
          <span className="text-xs uppercase tracking-wide text-gray-500">Dados agregados</span>
        </div>
        <HistorySummaryCards />
      </section>

      <section className="space-y-4 rounded-xl border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Linha do tempo de eventos</h2>
            <p className="text-sm text-gray-500">Eventos são exibidos em ordem decrescente de início.</p>
          </div>
        </div>
        <TimelineShell
          entries={timelineEntries}
          getTimestamp={(entry) => entry.startTime ?? entry.endTime ?? entry.packetDate}
          getEntryKey={(entry) => entry.id}
          renderEntry={(entry) => <TimelineEntryCard entry={entry} />}
          loading={loadingHistory}
          error={timelineError}
          notReady={!selectionReady}
          onRetry={handleRetry}
        />
        {hasMore ? (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingHistory}
              className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                loadingHistory
                  ? "cursor-not-allowed border border-neutral-300 bg-neutral-100 text-neutral-500"
                  : "border border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {loadingHistory ? "Carregando..." : "Carregar mais"}
            </button>
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-4">
        <h2 className="text-base font-medium text-gray-900">Tabela detalhada (opcional)</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Uma visualização tabular complementar poderá ser adicionada aqui.
        </p>
      </section>
    </div>
  );
}

export default IrrigationPlanHistoryPage;
